import { useState } from 'react'
import {
  AssignedPropertyValue,
  AssigneeType,
  Property,
  PropertyClient,
  PropertyValue,
} from 'api/clients/PropertyClient'
import { PropertyService } from 'api/services/PropertyService'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { buildErrorMessage } from 'utils'
import { convertPropertyValuesToAssignedPropertyValues } from 'common/PropertyValue/utils'

interface Props {
  assignee?: {
    id: string
    type: AssigneeType
  }
  workspaceId: string
  className?: string
  hasMore?: boolean
  loading?: boolean
  onLoadMore?: () => void
  onCreateAssignedProperty?: (property: Property) => Promise<void>
  onUpdateProperty?: (property: Property) => void
  onUpdateAssignedProperty?: (
    id: number | undefined,
    property: Property,
    assignedPropertyValues: AssignedPropertyValue[],
  ) => Promise<void>
  onAddPropertyValueToAssignedProperties?: (
    assignedPropertyId: number | undefined,
    propertyValue: PropertyValue,
  ) => void
  onRefreshAssignedProperty?: (property: Property) => void
}

export const usePropertiesList = ({
  assignee,
  onCreateAssignedProperty,
  onUpdateAssignedProperty,
  onUpdateProperty,
  onAddPropertyValueToAssignedProperties,
  workspaceId,
  onRefreshAssignedProperty,
}: Props) => {
  const [propertyPromptVisible, setPropertyPromptVisible] = useState<number | undefined>()
  const [propertyValuesPromptVisible, setPropertyValuesPromptVisible] = useState<Property | undefined>()

  const createProperty = async (propertyData: Property) => {
    const {
      data: { property },
    } = await PropertyService.createProperty(workspaceId, propertyData)

    createAssignedProperty(property)
  }

  const deleteProperty = async (propertyData: Property) => {
    const {
      data: { property },
    } = await PropertyService.deleteProperty(workspaceId, propertyData)

    return property
  }

  const updateProperty = async (selectedProperty: Property, isUndoAction?: boolean) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const response = await PropertyService.updateProperty(workspaceId, selectedProperty, assignee?.id!, isUndoAction)
    handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      const {
        data: { property },
      } = response
      onUpdateProperty && onUpdateProperty(property)
    }

    setPropertyPromptVisible(undefined)
    return response
  }

  const createAssignedProperty = async (property: Property) => {
    if (!onCreateAssignedProperty) return

    onCreateAssignedProperty(property)

    setPropertyValuesPromptVisible(property)
    setPropertyPromptVisible(undefined)
  }

  const createPropertyValue = async (assignedPropertyId: number | undefined, propertyValue: PropertyValue) => {
    const {
      data: { property_value },
    } = await PropertyService.createPropertyValue(workspaceId, propertyValue)

    onAddPropertyValueToAssignedProperties && onAddPropertyValueToAssignedProperties(assignedPropertyId, property_value)
    setPropertyValuesPromptVisible(undefined)
  }

  const updatePropertyValue = async (property: Property, propertyValue: PropertyValue) => {
    const response = await PropertyClient.updatePropertyValue(workspaceId, propertyValue)
    handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

    if (response && response.type === SUCCESS_RESPONSE_TYPE && onRefreshAssignedProperty) {
      onRefreshAssignedProperty(property)
    }

    setPropertyValuesPromptVisible(undefined)
  }

  const deletePropertyValue = async (property: Property, propertyValue: PropertyValue) => {
    const response = await PropertyClient.deletePropertyValue(workspaceId, propertyValue)
    handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

    if (response && response.type === SUCCESS_RESPONSE_TYPE && onRefreshAssignedProperty) {
      onRefreshAssignedProperty(property)
    }

    setPropertyValuesPromptVisible(undefined)
  }

  const createAssignedPropertyValue = (
    assignedPropertyId: number | undefined,
    property: Property,
    propertyValues: PropertyValue[],
  ) => {
    const assignedPropertyValues = convertPropertyValuesToAssignedPropertyValues(property, propertyValues)
    if (!assignedPropertyValues) return

    onUpdateAssignedProperty &&
      onUpdateAssignedProperty(assignedPropertyId, property, assignedPropertyValues as AssignedPropertyValue[])
    setPropertyValuesPromptVisible(undefined)
    setPropertyPromptVisible(undefined)
  }

  const openPropertyPrompt = (propertyId: number | undefined) => {
    setPropertyPromptVisible(propertyId)
    setPropertyValuesPromptVisible(undefined)
  }

  const closePropertyPrompt = () => {
    setPropertyPromptVisible(undefined)
  }

  const closePropertyValuesPrompt = () => {
    setPropertyValuesPromptVisible(undefined)
  }

  return {
    propertyPromptVisible,
    setPropertyPromptVisible,
    propertyValuesPromptVisible,
    setPropertyValuesPromptVisible,
    createProperty,
    updateProperty,
    deleteProperty,
    createAssignedProperty,
    createPropertyValue,
    updatePropertyValue,
    createAssignedPropertyValue,
    openPropertyPrompt,
    closePropertyPrompt,
    closePropertyValuesPrompt,
    deletePropertyValue,
  }
}
